import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTransactions } from './useTransactions';
import { useFetchAccounts } from './accounts/useAccounts.ts';
import { useExchangeRates } from './useExchangeRates';
import { useAuth } from '@/contexts/AuthContext';
import type { BalanceTrendPoint, TimePeriod } from '@/types/account-drilldown';

/**
 * Calculate time-based aggregation granularity based on period length
 */
function getAggregationGranularity(daysDiff: number): 'daily' | 'weekly' | 'monthly' {
  if (daysDiff < 30) return 'daily';
  if (daysDiff <= 90) return 'weekly';
  return 'monthly';
}

/**
 * Get date range for a time period
 */
function getDateRangeForPeriod(timePeriod: TimePeriod): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  
  switch (timePeriod) {
    case 'last7days': {
      const date = new Date(now);
      date.setDate(date.getDate() - 7);
      return { dateFrom: date.toISOString().split('T')[0], dateTo: today };
    }
    case 'last30days': {
      const date = new Date(now);
      date.setDate(date.getDate() - 30);
      return { dateFrom: date.toISOString().split('T')[0], dateTo: today };
    }
    case 'last90days': {
      const date = new Date(now);
      date.setDate(date.getDate() - 90);
      return { dateFrom: date.toISOString().split('T')[0], dateTo: today };
    }
    case 'alltime':
    default:
      return { dateFrom: '2000-01-01', dateTo: today };
  }
}

/**
 * Calculate balance at a specific date based on transaction history
 */
export function calculateBalanceAtDate(
  initialBalance: number,
  transactions: Array<{ transaction_date: string; type: string; amount: number }>,
  targetDate: string
): number {
  let balance = initialBalance;
  
  for (const tx of transactions) {
    if (tx.transaction_date <= targetDate) {
      if (tx.type === 'income') {
        balance += tx.amount;
      } else if (tx.type === 'expense') {
        balance -= tx.amount;
      }
    }
  }
  
  return balance;
}

/**
 * Aggregate balance data points by time granularity
 */
export function aggregateBalancePoints(
  points: BalanceTrendPoint[],
  granularity: 'daily' | 'weekly' | 'monthly'
): BalanceTrendPoint[] {
  if (granularity === 'daily' || points.length === 0) {
    return points;
  }
  
  const aggregated: BalanceTrendPoint[] = [];
  const grouped = new Map<string, BalanceTrendPoint[]>();
  
  for (const point of points) {
    const date = new Date(point.date);
    let key: string;
    
    if (granularity === 'weekly') {
      // Group by week (Monday as start of week)
      const dayOfWeek = date.getDay();
      const diff = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek; // Adjust to Monday
      const monday = new Date(date);
      monday.setDate(date.getDate() + diff);
      key = monday.toISOString().split('T')[0];
    } else {
      // Group by month
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
    }
    
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(point);
  }
  
  // Take the last point in each group (most recent balance for that period)
  for (const [key, groupPoints] of grouped.entries()) {
    const lastPoint = groupPoints[groupPoints.length - 1];
    aggregated.push({
      date: key,
      balance: lastPoint.balance,
      timestamp: new Date(key).getTime(),
    });
  }
  
  return aggregated.sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Hook to calculate balance trend for a single account
 */
function useSingleAccountBalanceTrend(accountId: string, timePeriod: TimePeriod) {
  const { data: accounts } = useFetchAccounts();
  const dateRange = useMemo(() => getDateRangeForPeriod(timePeriod), [timePeriod]);
  
  const { data: transactions, isLoading: transactionsLoading, error: transactionsError } = useTransactions({
    accountId,
    dateFrom: dateRange.dateFrom,
    dateTo: dateRange.dateTo,
  });
  
  const data = useMemo(() => {
    if (!accounts || !transactions) return undefined;
    
    const account = accounts.find(a => a.id === accountId);
    if (!account) return undefined;
    
    // Sort transactions by date ascending
    const sortedTxs = [...transactions].sort((a, b) => 
      a.transaction_date.localeCompare(b.transaction_date)
    );
    
    // Calculate balance at each transaction date
    const points: BalanceTrendPoint[] = [];
    let currentBalance = account.balance;
    
    // Calculate initial balance (balance before first transaction)
    if (sortedTxs.length > 0) {
      for (const tx of sortedTxs) {
        if (tx.type === 'income') {
          currentBalance -= tx.amount;
        } else if (tx.type === 'expense') {
          currentBalance += tx.amount;
        }
      }
    }
    
    // Add starting point
    const startDate = dateRange.dateFrom;
    points.push({
      date: startDate,
      balance: calculateBalanceAtDate(currentBalance, sortedTxs, startDate),
      timestamp: new Date(startDate).getTime(),
    });
    
    // Add point for each transaction date
    let balance = currentBalance;
    for (const tx of sortedTxs) {
      if (tx.type === 'income') {
        balance += tx.amount;
      } else if (tx.type === 'expense') {
        balance -= tx.amount;
      }
      
      // Only add if date is different from last point
      if (points.length === 0 || points[points.length - 1].date !== tx.transaction_date) {
        points.push({
          date: tx.transaction_date,
          balance,
          timestamp: new Date(tx.transaction_date).getTime(),
        });
      } else {
        // Update last point if same date
        points[points.length - 1].balance = balance;
      }
    }
    
    // Add ending point (today)
    const endDate = dateRange.dateTo;
    if (points.length === 0 || points[points.length - 1].date !== endDate) {
      points.push({
        date: endDate,
        balance: account.balance,
        timestamp: new Date(endDate).getTime(),
      });
    }
    
    // Determine aggregation granularity
    const daysDiff = Math.ceil(
      (new Date(dateRange.dateTo).getTime() - new Date(dateRange.dateFrom).getTime()) / (1000 * 60 * 60 * 24)
    );
    const granularity = getAggregationGranularity(daysDiff);
    
    // Aggregate points based on granularity
    return aggregateBalancePoints(points, granularity);
  }, [accounts, transactions, accountId, dateRange]);
  
  return {
    data,
    isLoading: transactionsLoading,
    error: transactionsError as Error | null,
  };
}

/**
 * Hook to calculate portfolio balance trend across all accounts
 */
function usePortfolioBalanceTrend(timePeriod: TimePeriod, baseCurrency: string = 'USD') {
  const { user } = useAuth();
  const { data: accounts, isLoading: accountsLoading } = useFetchAccounts();
  const { convert, isLoading: ratesLoading, error: ratesError } = useExchangeRates(baseCurrency);
  const dateRange = useMemo(() => getDateRangeForPeriod(timePeriod), [timePeriod]);
  
  const { data: allTransactions, isLoading: transactionsLoading, error: transactionsError } = useTransactions({
    dateFrom: dateRange.dateFrom,
    dateTo: dateRange.dateTo,
  });
  
  const data = useMemo(() => {
    if (!accounts || !allTransactions || ratesLoading) return undefined;
    
    // Group transactions by account
    const txByAccount = new Map<string, typeof allTransactions>();
    for (const tx of allTransactions) {
      if (!txByAccount.has(tx.account_id)) {
        txByAccount.set(tx.account_id, []);
      }
      txByAccount.get(tx.account_id)!.push(tx);
    }
    
    // Calculate balance trend for each account
    const accountTrends = new Map<string, BalanceTrendPoint[]>();
    
    for (const account of accounts) {
      const accountTxs = txByAccount.get(account.id) || [];
      const sortedTxs = [...accountTxs].sort((a, b) => 
        a.transaction_date.localeCompare(b.transaction_date)
      );
      
      // Calculate initial balance
      let initialBalance = account.balance;
      for (const tx of sortedTxs) {
        if (tx.type === 'income') {
          initialBalance -= tx.amount;
        } else if (tx.type === 'expense') {
          initialBalance += tx.amount;
        }
      }
      
      // Generate daily points
      const points: BalanceTrendPoint[] = [];
      let balance = initialBalance;
      let txIndex = 0;
      
      const startTime = new Date(dateRange.dateFrom).getTime();
      const endTime = new Date(dateRange.dateTo).getTime();
      
      for (let time = startTime; time <= endTime; time += 24 * 60 * 60 * 1000) {
        const date = new Date(time).toISOString().split('T')[0];
        
        // Apply transactions for this date
        while (txIndex < sortedTxs.length && sortedTxs[txIndex].transaction_date === date) {
          const tx = sortedTxs[txIndex];
          if (tx.type === 'income') {
            balance += tx.amount;
          } else if (tx.type === 'expense') {
            balance -= tx.amount;
          }
          txIndex++;
        }
        
        // Convert to base currency
        try {
          const convertedBalance = convert(balance, account.currency, baseCurrency);
          points.push({
            date,
            balance: convertedBalance,
            timestamp: time,
          });
        } catch (error) {
          // Skip if conversion fails
          console.warn(`Failed to convert ${account.currency} to ${baseCurrency}:`, error);
        }
      }
      
      accountTrends.set(account.id, points);
    }
    
    // Aggregate across all accounts by date
    const aggregatedPoints = new Map<string, number>();
    
    for (const points of accountTrends.values()) {
      for (const point of points) {
        const current = aggregatedPoints.get(point.date) || 0;
        aggregatedPoints.set(point.date, current + point.balance);
      }
    }
    
    // Convert to array
    const points: BalanceTrendPoint[] = Array.from(aggregatedPoints.entries())
      .map(([date, balance]) => ({
        date,
        balance,
        timestamp: new Date(date).getTime(),
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
    
    // Determine aggregation granularity
    const daysDiff = Math.ceil(
      (new Date(dateRange.dateTo).getTime() - new Date(dateRange.dateFrom).getTime()) / (1000 * 60 * 60 * 24)
    );
    const granularity = getAggregationGranularity(daysDiff);
    
    // Aggregate points based on granularity
    return aggregateBalancePoints(points, granularity);
  }, [accounts, allTransactions, convert, ratesLoading, baseCurrency, dateRange]);
  
  return {
    data,
    isLoading: accountsLoading || transactionsLoading || ratesLoading,
    error: (transactionsError || ratesError) as Error | null,
  };
}

/**
 * Hook to calculate balance trend over time
 * Supports both single account and portfolio modes
 * 
 * @param accountId - Account ID or 'portfolio' for all accounts
 * @param timePeriod - Time period for the trend
 * @param baseCurrency - Base currency for portfolio mode (default: USD)
 * @returns Balance trend data points with loading state and error
 */
export function useBalanceTrend(
  accountId: string | 'portfolio',
  timePeriod: TimePeriod,
  baseCurrency: string = 'USD'
) {
  const singleAccount = useSingleAccountBalanceTrend(
    accountId === 'portfolio' ? '' : accountId,
    timePeriod
  );
  
  const portfolio = usePortfolioBalanceTrend(timePeriod, baseCurrency);
  
  if (accountId === 'portfolio') {
    return portfolio;
  }
  
  return singleAccount;
}
